import React, { FunctionComponent, useState, useEffect, useRef } from 'react'
import style from './comparisonResultWrapper.css'
import NoEquipmentIcon from '../../assets/media/icons/no-equipment.svg'
import EmptyDotIcon from '../../assets/media/icons/empty-dot.svg'
import FilledDotIcon from '../../assets/media/icons/filled-dot.svg'
import ComparisonResultData from '../../entities/ComparisonResultData'
import ComparisonData from '../../entities/ComparisonData'
import useTranslation from '../../hooks/useTranslation'
import ComparisonResultSubcategory from '../../entities/ComparisonResultSubcategory'

export type Props = {
    resultData: ComparisonResultData
    comparisonData: ComparisonData
    viewProperties: Record<string, string>[]
}

const ComparisonResultWrapper: FunctionComponent<Props> = (props) => {
    const { resultData, comparisonData, viewProperties } = props
    const hideStandard = viewProperties && viewProperties['hideStandardOnCompare'] === 'true'
    const { t } = useTranslation()

    const [activeCategory, setActiveCategory] = useState(resultData?.categories[0]?.id)
    const categoryRef = useRef<HTMLUListElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!categoryRef.current || !wrapperRef.current) {
            return
        }

        const observer = new ResizeObserver(([entry]) => {
            const baseStickyTop = comparisonData.type === 'codes' ? 130 : 180
            wrapperRef.current!.style.setProperty('--sticky-column-header-top', `${baseStickyTop + entry.contentRect.height}px`)
        })

        observer.observe(categoryRef.current)
        return () => observer.disconnect()
    }, [comparisonData.type])

    const categoryContent = (
        <ul ref={categoryRef} className={style.compareResultCategories}>
            {
                resultData?.categories
                    .filter(category => !hideStandard || category.id !== 'standard')
                    .map(category => {
                        const classes = activeCategory === category.id
                            ? style.compareResultCategory + " " + style.compareResultCategoryActive
                            : style.compareResultCategory
                        return (
                            <li key={category.id} onClick={() => setActiveCategory(category.id)} className={classes} data-testid={`comparison-category-${category.id}`}>
                                {t('compare.categories.' + category.id, category.name)}
                            </li>
                        )
                    })
            }
        </ul>
    )

    const subcategories = resultData?.categories
        .filter(category => category.id === activeCategory)
        .flatMap(category => category.subCategory)

    const getHeadRows = (subcategory: ComparisonResultSubcategory, columnsNumber: number[]) => {
        if (!subcategory.name || subcategory.name.trim() === '') {
            return null
        }
        return (
            <tr className={style.resultRowCategory}>
                <td key={subcategory.id} colSpan={columnsNumber.length + 1}>
                    {t(`compare.subcategory.${subcategory.id}`, subcategory.name)}
                </td>
            </tr>
        )
    }

    const getBodyRows = (subcategory: ComparisonResultSubcategory) => {
        return subcategory.rows
            .filter(row => {
                if (!comparisonData.onlyDifferent) {
                    return true
                }
                return comparisonData.onlyDifferent && row.comparability === 'DIFFERENT'
            })
            .map(row => {
                const rowName = t('compare.rows.' + row.id, row.name)
                return (
                    <tr key={row.id}
                        className={row.comparability === 'DIFFERENT' ? style.resultRowDifferent : ""}
                        data-testid={`comparison-result-row-${row.id}`}
                    >
                        <td key={row.id} dangerouslySetInnerHTML={{ __html: rowName }} data-testid={`comparison-result-label-${row.id}`}></td>
                        {
                            row.columns.map((column, index) => {
                                if (column.type === "TEXT") {
                                    return (
                                        <td key={row.id + "_" + index} dangerouslySetInnerHTML={{ __html: column.value }}></td>
                                    )
                                } else {
                                    const columnTypeMap = {
                                        "EXTRA": (<EmptyDotIcon />),
                                        "STANDARD": (<FilledDotIcon />),
                                        "UNAVAILABLE": (<NoEquipmentIcon />),
                                    }
                                    return (
                                        <td key={row.id + "_" + index}>{columnTypeMap[column.type]}</td>
                                    )
                                }
                            })
                        }
                    </tr>
                )
            })
    }

    const subcategoryContent = (
        <ul className={style.compareResultCategoryViews}>
            <li className={style.compareResultCategoryView}>
                <table>
                    <tbody>
                        {subcategories.length > 0 && subcategories[0].rows.length > 0 && (
                            <tr key="column-header-row" className={style.columnHeaderRow}>
                                <td></td>
                                {
                                    subcategories[0].rows[0].columns.map((el, index) => (
                                        <td key={"header_" + (index + 1)}>{index + 1}</td>
                                    ))
                                }
                            </tr>
                        )}
                        {
                            subcategories.map((subcategory, index) => {
                                if (subcategory.rows.length === 0) {
                                    return undefined
                                }
                                const columnsNumber = subcategory.rows[0].columns.map((el, index) => index + 1)
                                const headRow = getHeadRows(subcategory, columnsNumber)
                                const bodyRows = getBodyRows(subcategory)
                                return (
                                    <React.Fragment key={`${activeCategory}-${subcategory.id}-${index}`}>
                                        {headRow}
                                        {bodyRows}
                                    </React.Fragment>
                                )
                            })
                        }
                    </tbody>
                </table>
            </li>
        </ul>
    )

    const showEquipmentInfoBlock = subcategories
        .flatMap(subcategory => subcategory.rows)
        .flatMap(row => row.columns)
        .filter(column => column.type === "EXTRA" || column.type === "STANDARD")
        .length > 0

    const infoBlock = showEquipmentInfoBlock
        ? (
            <div className={style.tableInfo}>
                <span> <FilledDotIcon /> - {t('compare.info.standard')}</span>
                <span> <EmptyDotIcon /> - {t('compare.info.optional')}</span>
                <span> <NoEquipmentIcon /> - {t('compare.info.notAvailable')}</span>
            </div>
        ) : undefined

    return (
        <div ref={wrapperRef} className={style.compareResultWrapper} data-comparison-mode={comparisonData.type}>
            {categoryContent}
            {subcategoryContent}
            {infoBlock}
        </div>
    )
}

export default ComparisonResultWrapper
